import { getDownloadURL, ref, uploadBytes, deleteObject, listAll } from 'firebase/storage';
import axios from 'axios';
import { auth } from '../firebaseConfig';
// Funções do Firestore importadas para a nova funcionalidade de notificação
import { doc, addDoc, updateDoc, deleteDoc, getDoc, collection, arrayUnion, arrayRemove, serverTimestamp, writeBatch, query, where, getDocs as getFirestoreDocs } from 'firebase/firestore';
import { parsePolesFromCSV } from '../utils/csvParser';
import { generatePendencyId } from '../utils/pendencyUtils';
import { generateNotificationReportPDF } from '../utils/pdfGenerator';
import { appId, STATUS } from '../constants/appConstants';
import { useRef, useEffect, useCallback } from 'react';
import { logActivity } from '../services/activityLogService';

/**
 * Hook customizado que encapsula toda a lógica de manipulação de dados (CRUD) com o Firebase.
 */
export const useAppHandlers = (props) => {
    const propsRef = useRef(props);
    useEffect(() => {
        propsRef.current = props;
    });

    const projectsCollectionPath = `artifacts/${appId}/public/data/projects`;
    const pendenciesCollectionPath = `artifacts/${appId}/public/data/pendencies`;
    const tradesCollectionPath = `artifacts/${appId}/public/data/trades`;

    /**
     * Cria notificações no Firestore para usuários com as roles especificadas.
     * @param {string[]} roles - Array de roles (ex: ['administrador', 'tecnico']).
     * @param {string} message - A mensagem da notificação.
     * @param {string} type - O tipo da notificação (para ícone e ação).
     * @param {string} relatedId - O ID do documento relacionado (projeto/pendência).
     */
    const createNotificationsForRoles = async (roles, message, type, relatedId) => {
        const { db } = propsRef.current;
        if (!db || !roles || roles.length === 0) return;

        try {
            const usersQuery = query(collection(db, 'users'), where('role', 'in', roles));
            const usersSnapshot = await getFirestoreDocs(usersQuery);
            
            if (usersSnapshot.empty) {
                console.log("Nenhum usuário encontrado para as roles:", roles);
                return;
            }

            const batch = writeBatch(db);
            usersSnapshot.forEach(userDoc => {
                const notificationRef = doc(collection(db, 'notifications'));
                batch.set(notificationRef, {
                    userId: userDoc.id,
                    message,
                    type,
                    relatedId,
                    createdAt: new Date().toISOString(),
                    read: false,
                });
            });
            await batch.commit();
        } catch (error) {
            console.error("Erro ao criar notificações em lote:", error);
        }
    };

    const onUpdatePendencyField = useCallback(async (pendencyId, field, value) => {
        const { db, setModalMessage } = propsRef.current;
        if (!pendencyId || !field) {
            setModalMessage('Dados insuficientes para atualizar a pendência.');
            return;
        }
    
        try {
            const docRef = doc(db, pendenciesCollectionPath, pendencyId);
            await updateDoc(docRef, { [field]: value });
    
            setModalMessage(`Tipo da pendência atualizado com sucesso!`);
        } catch (error) {
            console.error(`Erro ao atualizar o campo '${field}' da pendência:`, error);
            setModalMessage(`Erro ao atualizar o tipo da pendência.`);
        }
    }, []);

    // --- NOVA FUNÇÃO PARA LIMPAR NOTIFICAÇÕES ---
    /**
     * Exclui todas as notificações do usuário logado.
     */
    const onClearAllNotifications = useCallback(async () => {
        const { db, userData, setIsProcessing, setModalMessage } = propsRef.current;
        if (!db || !userData?.uid) {
            setModalMessage("Não foi possível limpar as notificações. Usuário não autenticado.");
            return;
        }

        setIsProcessing(true);
        try {
            // 1. Encontra todas as notificações do usuário atual.
            const q = query(collection(db, 'notifications'), where('userId', '==', userData.uid));
            const querySnapshot = await getFirestoreDocs(q);

            if (querySnapshot.empty) {
                setModalMessage("Não há notificações para limpar.");
                setIsProcessing(false);
                return;
            }

            // 2. Usa um batch para deletar todas de uma vez.
            const batch = writeBatch(db);
            querySnapshot.forEach((doc) => {
                batch.delete(doc.ref);
            });

            // 3. Executa a operação em lote.
            await batch.commit();
            setModalMessage("Todas as notificações foram limpas.");

            if (window.Android && window.Android.onClearNotifications) {
                try {
                    window.Android.onClearNotifications();
                } catch (e) {
                    console.error("[Android Bridge] Error calling onClearNotifications:", e);
                }
            }

        } catch (error) {
            console.error("Erro ao limpar notificações:", error);
            setModalMessage("Ocorreu um erro ao limpar as notificações.");
        } finally {
            setIsProcessing(false);
        }
    }, []);


    // ==================================
    // --- Handlers de Projeto ---
    // ==================================

    const onSaveProject = useCallback(async (projectData) => {
        const { db, storage, userData, setIsProcessing, setModalMessage, setOperationSuccess } = propsRef.current;
        if (!projectData.protocolo || !projectData.files.csvFile) {
            setModalMessage("Protocolo e arquivo CSV são obrigatórios para projetos.");
            return;
        }
        setIsProcessing(true);
        setModalMessage("Processando arquivo CSV do projeto...");
        try {
            const newPoles = await parsePolesFromCSV(projectData.files.csvFile, projectData.protocolo);
            const { csvFile, ...filesToUpload } = projectData.files;
            const docData = { ...projectData, postes: newPoles, totalPostes: newPoles.length, createdAt: new Date().toISOString(), createdBy: { uid: userData.uid, displayName: userData.displayName }, files: {}, comments: [] };
            
            const docRef = await addDoc(collection(db, projectsCollectionPath), docData);

            const fileUploadPromises = Object.entries(filesToUpload).map(async ([category, file]) => {
                if (!file) return null;
                const filesToUploadArr = Array.isArray(file) ? file : [file];
                const uploadedCategoryFiles = [];
                for (const singleFile of filesToUploadArr) {
                    const filePath = `projects/${docRef.id}/${category}/${singleFile.name}`;
                    await uploadBytes(ref(storage, filePath), singleFile);
                    uploadedCategoryFiles.push({ name: singleFile.name, path: filePath });
                }
                return { category, files: uploadedCategoryFiles };
            });

            const uploadedFiles = (await Promise.all(fileUploadPromises)).filter(Boolean);
            const filesMap = {};
            uploadedFiles.forEach(f => {
                filesMap[f.category] = f.category === 'outros' ? f.files : f.files[0];
            });
            await updateDoc(docRef, { files: filesMap });

            await logActivity('create', 'project', docRef.id, null, projectData.protocolo);

            await createNotificationsForRoles(
                ['administrador'],
                `Novo projeto cadastrado: ${projectData.protocolo}`,
                'projectUpdate',
                docRef.id
            );

            setModalMessage(`Projeto salvo! ${newPoles.length} postes adicionados.`);
            setOperationSuccess(true);
        } catch (error) {
            console.error("Erro ao salvar projeto:", error);
            setModalMessage(`Erro ao salvar projeto: ${error.message}`);
        } finally {
            setIsProcessing(false);
        }
    }, [projectsCollectionPath]);

    const onUpdateProject = useCallback(async (projectId, dataToUpdate, newFiles, newCategorizedFiles) => {
        const { db, storage, setIsProcessing, setModalMessage } = propsRef.current;
        setIsProcessing(true);
        try {
            const docRef = doc(db, projectsCollectionPath, projectId);
            const currentDoc = await getDoc(docRef);
            const oldStatus = currentDoc.data()?.status;
            const protocol = currentDoc.data()?.protocolo;
            const updatedFiles = { ...currentDoc.data()?.files };

            const otherFileUploadPromises = newFiles.map(async (file) => {
                const filePath = `projects/${projectId}/outros/${file.name}`;
                await uploadBytes(ref(storage, filePath), file);
                return { name: file.name, path: filePath };
            });
            const uploadedOtherFiles = await Promise.all(otherFileUploadPromises);
            if (uploadedOtherFiles.length > 0) {
                updatedFiles.outros = [...(updatedFiles.outros || []), ...uploadedOtherFiles];
            }
    
            for (const category in newCategorizedFiles) {
                const file = newCategorizedFiles[category];
                const filePath = `projects/${projectId}/${category}/${file.name}`;
                await uploadBytes(ref(storage, filePath), file);
                updatedFiles[category] = { name: file.name, path: filePath };
            }
    
            await updateDoc(docRef, { ...dataToUpdate, files: updatedFiles });

            const details = oldStatus !== dataToUpdate.status
                ? { statusChange: { from: oldStatus, to: dataToUpdate.status } }
                : null;

            await logActivity('update', 'project', projectId, details, protocol);

            if (oldStatus !== dataToUpdate.status) {
                await createNotificationsForRoles(
                    ['administrador'],
                    `Status do projeto ${protocol} alterado para "${dataToUpdate.status}"`,
                    'projectUpdate',
                    projectId
                );
            }

            setModalMessage("Projeto atualizado com sucesso!");
        } catch (error) {
            console.error("Erro ao atualizar projeto:", error);
            setModalMessage(`Erro ao atualizar projeto: ${error.message}`);
        } finally {
            setIsProcessing(false);
        }
    }, [projectsCollectionPath]);

    /**
     * Atualiza apenas o status e a data de status de um projeto (usado pelo Kanban drag-and-drop).
     * Registra a mudança no histórico de atividades do projeto.
     * @param {string} projectId - ID do projeto a ser atualizado.
     * @param {string} newStatus - Novo valor de status.
     * @param {string} newStatusDate - Data no formato YYYY-MM-DD.
     */
    const onUpdateProjectStatus = useCallback(async (projectId, newStatus, newStatusDate) => {
        const { db, setModalMessage } = propsRef.current;
        if (!projectId || !newStatus) {
            setModalMessage('Dados insuficientes para atualizar o status do projeto.');
            return;
        }
        try {
            const docRef = doc(db, projectsCollectionPath, projectId);
            const currentDoc = await getDoc(docRef);
            if (!currentDoc.exists()) throw new Error('Projeto não encontrado.');

            const oldStatus = currentDoc.data()?.status;
            const protocol = currentDoc.data()?.protocolo;

            await updateDoc(docRef, {
                status: newStatus,
                statusDate: newStatusDate,
            });

            const details = { statusChange: { from: oldStatus, to: newStatus } };
            await logActivity('update', 'project', projectId, details, protocol);

            await createNotificationsForRoles(
                ['administrador'],
                `Status do projeto ${protocol} alterado para "${newStatus}" via Kanban`,
                'projectUpdate',
                projectId
            );

            setModalMessage(`Status do projeto atualizado para "${newStatus}" com sucesso!`);
        } catch (error) {
            console.error('Erro ao atualizar status do projeto via Kanban:', error);
            setModalMessage(`Erro ao atualizar status: ${error.message}`);
        }
    }, [projectsCollectionPath]);

    const onReplaceProjectPoles = useCallback(async (projectId, newCsvFile) => {
        const { db, setIsProcessing, setModalMessage, setOperationSuccess } = propsRef.current;

        if (!projectId || !newCsvFile) {
            setModalMessage("ID do projeto ou arquivo CSV não fornecido.");
            return;
        }

        setIsProcessing(true);
        setModalMessage("Validando e processando novo arquivo CSV...");

        try {
            const projectRef = doc(db, projectsCollectionPath, projectId);
            const projectSnap = await getDoc(projectRef);

            if (!projectSnap.exists()) {
                throw new Error("Projeto não encontrado.");
            }

            const projectProtocol = projectSnap.data().protocolo;
            if (!projectProtocol) {
                throw new Error("O protocolo do projeto não foi encontrado. A validação do CSV não pode continuar.");
            }

            const newPoles = await parsePolesFromCSV(newCsvFile, projectProtocol);

            await updateDoc(projectRef, {
                postes: newPoles,
                totalPostes: newPoles.length,
                updatedAt: new Date().toISOString(),
            });

            setModalMessage(`Postes do projeto atualizados com sucesso! ${newPoles.length} postes foram carregados.`);
            setOperationSuccess(true);

        } catch (error) {
            console.error("Erro ao substituir postes do projeto:", error);
            setModalMessage(`Erro ao substituir postes: ${error.message}`);
        } finally {
            setIsProcessing(false);
        }
    }, [projectsCollectionPath]);

    const onDeleteProject = useCallback(async (projectId) => {
        const { db, storage, setIsProcessing, setModalMessage, setOperationSuccess } = propsRef.current;
        if (!storage) {
            setModalMessage("Serviço de armazenamento não está pronto. Tente novamente.");
            return;
        }
        setIsProcessing(true);
        setModalMessage("Excluindo projeto e arquivos...");
        try {
            const docRef = doc(db, projectsCollectionPath, projectId);
            const currentDoc = await getDoc(docRef);
            const protocol = currentDoc.data()?.protocolo;

            const projectFolderRef = ref(storage, `projects/${projectId}`);
            const res = await listAll(projectFolderRef);
            for (const folderRef of res.prefixes) {
                const folderItems = await listAll(folderRef);
                await Promise.all(folderItems.items.map(itemRef => deleteObject(itemRef)));
            }
            await deleteDoc(docRef);
            await logActivity('delete', 'project', projectId, null, protocol);
            setModalMessage("Projeto excluído com sucesso!");
            setOperationSuccess(true);
        } catch (error) {
            console.error("Erro ao excluir projeto:", error);
            setModalMessage(`Erro ao excluir: ${error.message}`);
        } finally {
            setIsProcessing(false);
        }
    }, [projectsCollectionPath]);

    const notifyMentionedUsers = useCallback(async (userIds, projectProtocol, projectId) => {
        const { db, userData } = propsRef.current;
        if (!db || !userIds || userIds.length === 0) return;
        try {
            const batch = writeBatch(db);
            // Filtra UIDs válidos e remove duplicatas
            const uniqueUids = Array.from(new Set(userIds.filter(Boolean)));
            
            uniqueUids.forEach(uid => {
                // Ignora se o UID for do próprio usuário que comentou
                if (uid === (userData.id || userData.uid)) return;
                
                const notificationRef = doc(collection(db, 'notifications'));
                batch.set(notificationRef, {
                    userId: uid,
                    message: `${userData.displayName} mencionou você no comentário do projeto ${projectProtocol}`,
                    type: 'projectMention',
                    relatedId: projectId,
                    createdAt: new Date().toISOString(),
                    read: false,
                });
            });
            await batch.commit();
        } catch (error) {
            console.error("Erro ao notificar usuários mencionados:", error);
        }
    }, []);

    const onAddProjectComment = useCallback(async (projectId, commentText, parentId = null, mentionedUids = []) => {
        const { db, userData, setModalMessage } = propsRef.current;
        const newComment = {
            id: Math.random().toString(36).substring(2, 11),
            text: commentText,
            author: { uid: userData.uid, displayName: userData.displayName },
            createdAt: new Date().toISOString(),
            parentId: parentId
        };
        try {
            const docRef = doc(db, projectsCollectionPath, projectId);
            await updateDoc(docRef, { comments: arrayUnion(newComment) });
            if (mentionedUids && mentionedUids.length > 0) {
                const projectSnap = await getDoc(docRef);
                const protocol = projectSnap.data()?.protocolo || 'Sem Protocolo';
                await notifyMentionedUsers(mentionedUids, protocol, projectId);
            }
        } catch (error) {
            console.error("Erro ao adicionar comentário:", error);
            setModalMessage("Falha ao adicionar comentário.");
        }
    }, [projectsCollectionPath, notifyMentionedUsers]);
    
    const onUpdateProjectComment = useCallback(async (projectId, updatedComment, mentionedUids = []) => {
        const { db, setModalMessage } = propsRef.current;
        const docRef = doc(db, projectsCollectionPath, projectId);
        try {
            const projectDoc = await getDoc(docRef);
            const comments = projectDoc.data().comments || [];
            const newComments = comments.map(c => 
                (c.id === updatedComment.id || c.createdAt === updatedComment.createdAt) ? updatedComment : c
            );
            await updateDoc(docRef, { comments: newComments });
            if (mentionedUids && mentionedUids.length > 0) {
                const protocol = projectDoc.data()?.protocolo || 'Sem Protocolo';
                await notifyMentionedUsers(mentionedUids, protocol, projectId);
            }
        } catch (error) {
            console.error("Erro ao atualizar comentário:", error);
            setModalMessage("Falha ao atualizar comentário.");
        }
    }, [projectsCollectionPath, notifyMentionedUsers]);

    const onDeleteProjectComment = useCallback(async (projectId, commentToDelete) => {
        const { db, setModalMessage } = propsRef.current;
        const docRef = doc(db, projectsCollectionPath, projectId);
        try {
            const projectDoc = await getDoc(docRef);
            const comments = projectDoc.data().comments || [];
            const targetId = commentToDelete.id || commentToDelete.createdAt;
            
            const getDescendantIds = (parentId, allComments) => {
                let descendantIds = [];
                const directChildren = allComments.filter(c => c.parentId === parentId);
                for (const child of directChildren) {
                    const childId = child.id || child.createdAt;
                    descendantIds.push(childId);
                    descendantIds = [...descendantIds, ...getDescendantIds(childId, allComments)];
                }
                return descendantIds;
            };
            
            const idsToDelete = [targetId, ...getDescendantIds(targetId, comments)];
            const newComments = comments.filter(c => {
                const commentId = c.id || c.createdAt;
                return !idsToDelete.includes(commentId);
            });
            await updateDoc(docRef, { comments: newComments });
        } catch (error) {
            console.error("Erro ao excluir comentário:", error);
            setModalMessage("Falha ao excluir comentário.");
        }
    }, [projectsCollectionPath]);

    const onDeleteProjectFile = useCallback(async (projectId, fileToDelete) => {
        const { db, storage, setModalMessage } = propsRef.current;
        if (!storage) {
            setModalMessage("Serviço de armazenamento não está pronto. Tente novamente.");
            return;
        }
        setModalMessage("Excluindo arquivo...");
        try {
            await deleteObject(ref(storage, fileToDelete.path));
            const docRef = doc(db, projectsCollectionPath, projectId);
            const currentDoc = await getDoc(docRef);
            if (currentDoc.exists()) {
                const updatedFiles = { ...currentDoc.data().files };
                for (const category in updatedFiles) {
                    if (Array.isArray(updatedFiles[category])) {
                        updatedFiles[category] = updatedFiles[category].filter(f => f.path !== fileToDelete.path);
                    } else if (updatedFiles[category]?.path === fileToDelete.path) {
                        delete updatedFiles[category];
                    }
                }
                await updateDoc(docRef, { files: updatedFiles });
            }
            setModalMessage("Arquivo excluído com sucesso!");
        } catch (error) {
            console.error("Erro ao excluir arquivo:", error);
            setModalMessage("Não foi possível excluir o arquivo.");
        }
    }, [projectsCollectionPath]);


    // ==================================
    // --- Handlers de Pendência ---
    // ==================================

    const notifyMentionedUsersInPendency = useCallback(async (userIds, pendencyDesc, pendencyId) => {
        const { db, userData } = propsRef.current;
        if (!db || !userIds || userIds.length === 0) return;
        try {
            const batch = writeBatch(db);
            // Filtra UIDs válidos e remove duplicatas
            const uniqueUids = Array.from(new Set(userIds.filter(Boolean)));
            const pendencyIdentifier = generatePendencyId(pendencyId);
            
            uniqueUids.forEach(uid => {
                // Ignora se o UID for do próprio usuário que comentou
                if (uid === (userData.id || userData.uid)) return;
                
                const notificationRef = doc(collection(db, 'notifications'));
                batch.set(notificationRef, {
                    userId: uid,
                    message: `${userData.displayName} mencionou você no comentário da pendência #${pendencyIdentifier}`,
                    type: 'pendencyMention',
                    relatedId: pendencyId,
                    createdAt: new Date().toISOString(),
                    read: false,
                });
            });
            await batch.commit();
        } catch (error) {
            console.error("Erro ao notificar usuários mencionados na pendência:", error);
        }
    }, []);

    const onAddPendencyComment = useCallback(async (pendencyId, commentText, parentId = null, mentionedUids = []) => {
        const { db, userData, setModalMessage } = propsRef.current;
        const newComment = {
            id: Math.random().toString(36).substring(2, 11),
            text: commentText,
            author: { uid: userData.uid, displayName: userData.displayName },
            createdAt: new Date().toISOString(),
            parentId: parentId
        };
        try {
            const docRef = doc(db, pendenciesCollectionPath, pendencyId);
            await updateDoc(docRef, { comments: arrayUnion(newComment) });
            if (mentionedUids && mentionedUids.length > 0) {
                const pendencySnap = await getDoc(docRef);
                const desc = pendencySnap.data()?.descricao || 'Sem Descrição';
                await notifyMentionedUsersInPendency(mentionedUids, desc, pendencyId);
            }
        } catch (error) {
            console.error("Erro ao adicionar comentário de pendência:", error);
            setModalMessage("Falha ao adicionar comentário.");
        }
    }, [pendenciesCollectionPath, notifyMentionedUsersInPendency]);

    const onUpdatePendencyComment = useCallback(async (pendencyId, updatedComment, mentionedUids = []) => {
        const { db, setModalMessage } = propsRef.current;
        const docRef = doc(db, pendenciesCollectionPath, pendencyId);
        try {
            const pendencyDoc = await getDoc(docRef);
            const comments = pendencyDoc.data().comments || [];
            const newComments = comments.map(c => 
                (c.id === updatedComment.id || c.createdAt === updatedComment.createdAt) ? updatedComment : c
            );
            await updateDoc(docRef, { comments: newComments });
            if (mentionedUids && mentionedUids.length > 0) {
                const desc = pendencyDoc.data()?.descricao || 'Sem Descrição';
                await notifyMentionedUsersInPendency(mentionedUids, desc, pendencyId);
            }
        } catch (error) {
            console.error("Erro ao atualizar comentário de pendência:", error);
            setModalMessage("Falha ao atualizar comentário.");
        }
    }, [pendenciesCollectionPath, notifyMentionedUsersInPendency]);

    const onDeletePendencyComment = useCallback(async (pendencyId, commentToDelete) => {
        const { db, setModalMessage } = propsRef.current;
        const docRef = doc(db, pendenciesCollectionPath, pendencyId);
        try {
            const pendencyDoc = await getDoc(docRef);
            const comments = pendencyDoc.data().comments || [];
            const targetId = commentToDelete.id || commentToDelete.createdAt;
            
            const getDescendantIds = (parentId, allComments) => {
                let descendantIds = [];
                const directChildren = allComments.filter(c => c.parentId === parentId);
                for (const child of directChildren) {
                    const childId = child.id || child.createdAt;
                    descendantIds.push(childId);
                    descendantIds = [...descendantIds, ...getDescendantIds(childId, allComments)];
                }
                return descendantIds;
            };
            
            const idsToDelete = [targetId, ...getDescendantIds(targetId, comments)];
            const newComments = comments.filter(c => {
                const commentId = c.id || c.createdAt;
                return !idsToDelete.includes(commentId);
            });
            await updateDoc(docRef, { comments: newComments });
        } catch (error) {
            console.error("Erro ao excluir comentário de pendência:", error);
            setModalMessage("Falha ao excluir comentário.");
        }
    }, [pendenciesCollectionPath]);

    const onSavePendency = useCallback(async (pendencyData, files) => {
        const { db, storage, userData, setIsProcessing, setModalMessage, onPendencyCreated } = propsRef.current;
        setIsProcessing(true);
        try {
            const comments = [];
            if (pendencyData.observacoes && pendencyData.observacoes.trim() !== '') {
                comments.push({
                    id: Math.random().toString(36).substring(2, 11),
                    text: pendencyData.observacoes,
                    author: { uid: userData.uid, displayName: userData.displayName },
                    createdAt: new Date().toISOString(),
                    parentId: null
                });
            }

            const dataToSave = { 
                ...pendencyData, 
                files: [], 
                comments: comments,
                status: 'Pendente', 
                createdAt: new Date().toISOString(), 
                createdBy: { uid: userData.uid, displayName: userData.displayName } 
            };
            const docRef = await addDoc(collection(db, pendenciesCollectionPath), dataToSave);
            
            const fileUploadPromises = files.map(async (file) => {
                const filePath = `pendencies/${docRef.id}/${file.name}`;
                await uploadBytes(ref(storage, filePath), file);
                return { name: file.name, path: filePath, url: await getDownloadURL(ref(storage, filePath)) };
            });
            
            const uploadedFiles = await Promise.all(fileUploadPromises);
            await updateDoc(docRef, { files: uploadedFiles });

            const pendencyIdentifier = generatePendencyId(docRef.id);
            await logActivity('create', 'pendency', docRef.id, null, pendencyIdentifier);
            
            await createNotificationsForRoles(
                ['administrador', 'tecnico'],
                `Nova pendência em ${pendencyData.cidade}: ${pendencyData.descricao.substring(0, 50)}...`,
                'pendencyUpdate',
                docRef.id
            );

            if (onPendencyCreated) {
                onPendencyCreated(docRef.id);
            }

        } catch (error) {
            console.error("Erro ao salvar pendência:", error);
            setModalMessage(`Erro ao salvar pendência: ${error.message}`);
        } finally {
            setIsProcessing(false);
        }
    }, []);

    const onUpdatePendency = useCallback(async (pendencyId, dataToUpdate, newFiles, oldStatus) => {
        const { db, storage, setIsProcessing, setModalMessage, triggerCelebration, setOperationSuccess } = propsRef.current;
        setIsProcessing(true);
        try {
            const docRef = doc(db, pendenciesCollectionPath, pendencyId);
            const currentDoc = await getDoc(docRef);
            const existingFiles = currentDoc.data()?.files || [];
            
            const fileUploadPromises = newFiles.map(async (file) => {
                const filePath = `pendencies/${pendencyId}/${file.name}`;
                await uploadBytes(ref(storage, filePath), file);
                return { name: file.name, path: filePath, url: await getDownloadURL(ref(storage, filePath)) };
            });
            
            const uploadedFiles = await Promise.all(fileUploadPromises);
            await updateDoc(docRef, { ...dataToUpdate, files: [...existingFiles, ...uploadedFiles] });

            const isFinished = dataToUpdate.status === 'Executado' && oldStatus !== 'Executado';
            const activityType = isFinished ? 'finish' : 'update';

            const details = oldStatus !== dataToUpdate.status
                ? { statusChange: { from: oldStatus, to: dataToUpdate.status } }
                : null;

            const pendencyIdentifier = generatePendencyId(pendencyId);
            await logActivity(activityType, 'pendency', pendencyId, details, pendencyIdentifier);
            
            if (oldStatus !== dataToUpdate.status) {
                const pendencyDoc = await getDoc(docRef);
                await createNotificationsForRoles(
                    ['administrador', 'tecnico'],
                    `Pendência "${pendencyDoc.data()?.descricao.substring(0, 30)}..." foi atualizada para "${dataToUpdate.status}"`,
                    'pendencyUpdate',
                    pendencyId
                );
            }

            if (dataToUpdate.status === 'Executado' && oldStatus !== 'Executado') {
                if (triggerCelebration) {
                    triggerCelebration();
                }

                // Desafixa o card automaticamente se ele for executado
                const settingsRef = doc(db, `artifacts/${appId}/public/data/settings`, 'config');
                const settingsSnap = await getDoc(settingsRef);
                if (settingsSnap.exists() && settingsSnap.data().pinnedPendencies?.includes(pendencyId)) {
                    await updateDoc(settingsRef, { pinnedPendencies: arrayRemove(pendencyId) });
                }
            }

            setModalMessage("Pendência atualizada com sucesso!");
            setOperationSuccess(true);
        } catch (error) {
            console.error("Erro ao atualizar pendência:", error);
            setModalMessage(`Erro ao atualizar pendência: ${error.message}`);
        } finally {
            setIsProcessing(false);
        }
    }, []);
    
    const onDeletePendency = useCallback(async (pendencyId) => {
        const { db, storage, setIsProcessing, setModalMessage, setOperationSuccess } = propsRef.current;
        if (!storage) {
            setModalMessage("Serviço de armazenamento não está pronto. Tente novamente.");
            return;
        }
        setIsProcessing(true);
        try {
            const pendencyFolderRef = ref(storage, `pendencies/${pendencyId}`);
            const res = await listAll(pendencyFolderRef);
            await Promise.all(res.items.map(itemRef => deleteObject(itemRef)));

            const pendencyIdentifier = generatePendencyId(pendencyId);
            await deleteDoc(doc(db, pendenciesCollectionPath, pendencyId));
            await logActivity('delete', 'pendency', pendencyId, null, pendencyIdentifier);
            setModalMessage("Pendência excluída com sucesso!");
            setOperationSuccess(true);
        } catch (error) {
            console.error("Erro ao excluir pendência:", error);
            setModalMessage(`Erro ao excluir: ${error.message}`);
        } finally {
            setIsProcessing(false);
        }
    }, []);

    const onDeletePendencyFile = useCallback(async (pendencyId, fileToDelete) => {
        const { storage, db, setModalMessage } = propsRef.current;
        if (!storage) {
            setModalMessage("Serviço de armazenamento não está pronto. Tente novamente.");
            return;
        }
        try {
            await deleteObject(ref(storage, fileToDelete.path));
            const docRef = doc(db, pendenciesCollectionPath, pendencyId);
            await updateDoc(docRef, { files: arrayRemove(fileToDelete) });
            setModalMessage("Arquivo da pendência excluído.");
        } catch (error) {
            console.error("Erro ao excluir arquivo da pendência:", error);
            setModalMessage("Não foi possível excluir o arquivo da pendência.");
        }
    }, []);

    // Optimized: Wrapped in useCallback to provide a stable function reference.
    // This allows child components (like PendencyListItem) to be memoized and avoid unnecessary re-renders.
    const onTogglePendencyPin = useCallback(async (pendencyId) => {
        const { db, setModalMessage } = propsRef.current;
        const settingsRef = doc(db, `artifacts/${appId}/public/data/settings`, 'config');
        try {
            const settingsSnap = await getDoc(settingsRef);
            const currentPinned = settingsSnap.exists() ? settingsSnap.data().pinnedPendencies || [] : [];
            
            let newPinned = [];
            if (currentPinned.includes(pendencyId)) {
                newPinned = currentPinned.filter(id => id !== pendencyId);
            } else {
                if (currentPinned.length < 6) {
                    newPinned = [...currentPinned, pendencyId];
                } else {
                    setModalMessage("Você pode fixar no máximo 6 pendências.");
                    return; // Early exit
                }
            }
            await updateDoc(settingsRef, { pinnedPendencies: newPinned });
        } catch (error) {
            console.error("Erro ao fixar/desafixar pendência:", error);
            setModalMessage("Ocorreu um erro ao tentar fixar a pendência.");
        }
    }, []);

    const onUpdateTrade = useCallback(async (tradeId, dataToUpdate) => {
        const { db, userData, setIsProcessing, setModalMessage, setOperationSuccess } = propsRef.current;
        if (!tradeId) {
            setModalMessage("ID da troca não fornecido.");
            return;
        }
        setIsProcessing(true);
        const tradeRef = doc(db, tradesCollectionPath, tradeId);
        try {
            const tradeDoc = await getDoc(tradeRef);
            const serviceNote = tradeDoc.data()?.serviceNote;
            const finalData = {
                ...dataToUpdate,
                updatedAt: new Date().toISOString(),
                updatedBy: { uid: userData.uid, displayName: userData.displayName }
            };
            await updateDoc(tradeRef, finalData);
            await logActivity('update', 'trade', tradeId, null, serviceNote);
            setModalMessage("Observações salvas com sucesso!");
            setOperationSuccess(true);
        } catch (error) {
            console.error("Erro ao atualizar a troca:", error);
            setModalMessage(`Ocorreu um erro ao salvar: ${error.message}`);
            setOperationSuccess(false);
        } finally {
            setIsProcessing(false);
        }
    }, []);

    const onToggleTradeCancellation = useCallback(async (tradeId, currentStatus) => {
        const { db, setModalMessage, setIsProcessing } = propsRef.current;
        if (!tradeId) {
            setModalMessage("ID da troca não fornecido.");
            return;
        }
        setIsProcessing(true);
        const tradeRef = doc(db, tradesCollectionPath, tradeId);
        try {
            await updateDoc(tradeRef, {
                isCanceled: !currentStatus,
            });
            setModalMessage(`Nota de serviço ${!currentStatus ? 'cancelada' : 'reativada'} com sucesso.`);
        } catch (error) {
            console.error("Erro ao atualizar o status da troca:", error);
            setModalMessage("Ocorreu um erro ao alterar o status da nota de serviço.");
        } finally {
            setIsProcessing(false);
        }
    }, []);

    const onFileDownload = useCallback(async (filePath, fileName) => {
        const { setModalMessage } = propsRef.current;
        setModalMessage("Iniciando download...");

        const isProduction = process.env.NODE_ENV === 'production';
        const API_BASE_URL = isProduction ? '/api' : (process.env.REACT_APP_LOCAL_API_URL || 'http://localhost:3001');
        const proxyUrl = `${API_BASE_URL}/downloadProxy`;

        try {
            // Get the current user's ID token to authenticate the request
            const token = await auth.currentUser?.getIdToken();
            const headers = token ? { Authorization: `Bearer ${token}` } : {};

            const response = await axios.get(proxyUrl, {
                params: { path: filePath },
                responseType: 'blob', // Important for file downloads
                headers,
            });

            // Create a blob URL and trigger the download
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();

            // Cleanup
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            setTimeout(() => setModalMessage(""), 500);

        } catch (error) {
            console.error("Erro ao baixar o arquivo:", error);
            setModalMessage("Não foi possível baixar o arquivo. O servidor pode estar indisponível ou o arquivo pode não existir.");
        }
    }, []);

    const onAssignNotification = useCallback(async (notificationId, user) => {
        const { db, setIsProcessing, setModalMessage } = propsRef.current;
        if (!notificationId || !user) {
            setModalMessage("ID da notificação ou usuário não fornecido.");
            return;
        }
        setIsProcessing(true);
        const notificationRef = doc(db, `artifacts/${appId}/public/data/irregularity_notifications`, notificationId);
        try {
            await updateDoc(notificationRef, {
                assignedTechnician: user.uid,
                status: STATUS.EM_EXECUCAO,
                updatedAt: serverTimestamp(),
                updatedBy: user.uid,
            });
            setModalMessage("Notificação assumida com sucesso!");
            return true;
        } catch (error) {
            console.error("Erro ao assumir notificação:", error);
            setModalMessage(`Ocorreu um erro: ${error.message}`);
            return false;
        } finally {
            setIsProcessing(false);
        }
    }, []);

    const onCloseNotification = useCallback(async (notification, isNetworkless, closureComments) => {
        const { db, userData, setIsProcessing, setModalMessage, setOperationSuccess } = propsRef.current;
        if (!notification?.id) {
            setModalMessage("ID da notificação não fornecido.");
            return;
        }
        setIsProcessing(true);
        const notificationRef = doc(db, `artifacts/${appId}/public/data/irregularity_notifications`, notification.id);
        try {
            let dataToUpdate;

            // Build technician notes update if comments provided
            const technicianNotesUpdate = closureComments
                ? (notification.technicianNotes ? `${notification.technicianNotes}\n\n[Encerramento]: ${closureComments}` : `[Encerramento]: ${closureComments}`)
                : undefined;

            // A 'tecnico_campo' has restricted write permissions.
            if (userData.role === 'tecnico_campo') {
                dataToUpdate = {
                    status: STATUS.FINALIZADA,
                    updatedAt: serverTimestamp(),
                    updatedBy: userData.uid,
                    isNetworkless: isNetworkless || false,
                };
                if (technicianNotesUpdate) {
                    dataToUpdate.technicianNotes = technicianNotesUpdate;
                }
                // If the notification is unassigned, the field tech who closes it gets assigned.
                if (!notification.assignedTechnician) {
                    dataToUpdate.assignedTechnician = userData.uid;
                }
            } else {
                // For admin/tecnico, more fields can be updated.
                dataToUpdate = {
                    status: STATUS.FINALIZADA,
                    responseStatus: 'Não respondida',
                    closedAt: serverTimestamp(),
                    updatedAt: serverTimestamp(),
                    updatedBy: userData.uid,
                    isNetworkless: isNetworkless || false,
                };
                if (technicianNotesUpdate) {
                    dataToUpdate.technicianNotes = technicianNotesUpdate;
                }
                if (!notification.assignedTechnician) {
                    dataToUpdate.assignedTechnician = userData.uid;
                }
            }

            await updateDoc(notificationRef, dataToUpdate);

            // An admin should be notified that a notification has been closed and needs review.
            await createNotificationsForRoles(
                ['administrador'],
                `A notificação ${notification.notificationNumber} foi finalizada por ${userData.displayName} e aguarda revisão.`,
                'notificationClosed',
                notification.id
            );

            setModalMessage("Notificação encerrada com sucesso!");
            setOperationSuccess(true);
        } catch (error) {
            console.error("Erro ao encerrar notificação:", error);
            setModalMessage(`Ocorreu um erro ao encerrar a notificação: ${error.message}`);
            setOperationSuccess(false);
        } finally {
            setIsProcessing(false);
        }
    }, []);

    const onRestoreNotification = useCallback(async (notificationId) => {
        const { db, userData, setIsProcessing, setModalMessage, setOperationSuccess } = propsRef.current;
        if (!notificationId) return;

        setIsProcessing(true);
        const notificationRef = doc(db, `artifacts/${appId}/public/data/irregularity_notifications`, notificationId);

        try {
            await updateDoc(notificationRef, {
                status: STATUS.PENDENTE,
                assignedTechnician: '', // Remove assignment
                updatedAt: serverTimestamp(),
                updatedBy: userData.uid,
                // We might want to clear 'closedAt' or 'responseStatus' if needed, but let's keep history or reset if required.
                // Resetting responseStatus might be good if it was auto-set.
                responseStatus: 'Não respondida'
            });

            await logActivity('restore', 'notification', notificationId, null);
            setModalMessage("Notificação restaurada com sucesso!");
            setOperationSuccess(true);
        } catch (error) {
            console.error("Erro ao restaurar notificação:", error);
            setModalMessage(`Erro ao restaurar: ${error.message}`);
        } finally {
            setIsProcessing(false);
        }
    }, []);

    const onMarkAsAnswered = useCallback(async (notificationId) => {
        const { db, userData, setIsProcessing, setModalMessage, setOperationSuccess } = propsRef.current;
        if (!notificationId) {
            setModalMessage("ID da notificação não fornecido.");
            return;
        }
        setIsProcessing(true);
        const notificationRef = doc(db, `artifacts/${appId}/public/data/irregularity_notifications`, notificationId);
        try {
            await updateDoc(notificationRef, {
                responseStatus: 'Respondida',
                respondedAt: serverTimestamp(),
                respondedBy: userData.uid,
            });
            setModalMessage("Notificação marcada como respondida com sucesso!");
            setOperationSuccess(true);
        } catch (error) {
            console.error("Erro ao marcar notificação como respondida:", error);
            setModalMessage(`Ocorreu um erro: ${error.message}`);
            setOperationSuccess(false);
        } finally {
            setIsProcessing(false);
        }
    }, []);

    const onGenerateNotificationReport = useCallback(async (notification, photos, usersMap) => {
        const { db, userData, setIsProcessing, setModalMessage, setOperationSuccess } = propsRef.current;
        if (!notification || !photos || !usersMap) {
            setModalMessage("Dados insuficientes para gerar o relatório.");
            return;
        }
        setIsProcessing(true);
        try {
            await generateNotificationReportPDF(notification, photos, usersMap);

            const notificationRef = doc(db, `artifacts/${appId}/public/data/irregularity_notifications`, notification.id);
            await updateDoc(notificationRef, {
                responseStatus: 'Respondida',
                respondedAt: serverTimestamp(),
                respondedBy: userData.uid,
            });

            setModalMessage("Relatório gerado e notificação marcada como respondida.");
            setOperationSuccess(true);

        } catch (error) {
            console.error("Erro ao gerar relatório da notificação:", error);
            setModalMessage(`Ocorreu um erro ao gerar o relatório: ${error.message}`);
            setOperationSuccess(false);
        } finally {
            setIsProcessing(false);
        }
    }, []);

    const onAbandonNotification = useCallback(async (notificationId) => {
        const { db, userData, setIsProcessing, setModalMessage } = propsRef.current;
        if (!notificationId) {
            setModalMessage("ID da notificação não fornecido.");
            return;
        }
        setIsProcessing(true);
        const notificationRef = doc(db, `artifacts/${appId}/public/data/irregularity_notifications`, notificationId);
        try {
            await updateDoc(notificationRef, {
                assignedTechnician: '',
                status: STATUS.PENDENTE,
                updatedAt: serverTimestamp(),
                updatedBy: userData.uid,
            });
            setModalMessage("Notificação abandonada com sucesso.");
        } catch (error) {
            console.error("Erro ao abandonar notificação:", error);
            setModalMessage(`Ocorreu um erro: ${error.message}`);
        } finally {
            setIsProcessing(false);
        }
    }, []);

    const onSaveNotification = useCallback(async (notificationData, files) => {
        const { db, storage, userData, setIsProcessing, setModalMessage, setOperationSuccess } = propsRef.current;
        setIsProcessing(true);
        try {
            // Check for duplicate notification number
            if (notificationData.notificationNumber) {
                const q = query(
                    collection(db, `artifacts/${appId}/public/data/irregularity_notifications`),
                    where("notificationNumber", "==", notificationData.notificationNumber)
                );
                const querySnapshot = await getFirestoreDocs(q);
                if (!querySnapshot.empty) {
                    setModalMessage(`Já existe uma notificação cadastrada com o número ${notificationData.notificationNumber}.`);
                    setIsProcessing(false);
                    return;
                }
            }

            const dataToSave = {
                ...notificationData,
                createdAt: serverTimestamp(),
                createdBy: userData.uid,
                status: notificationData.status || STATUS.PENDENTE,
                attachments: [],
            };

            const docRef = await addDoc(collection(db, `artifacts/${appId}/public/data/irregularity_notifications`), dataToSave);

            if (files && files.length > 0) {
                setModalMessage(`Enviando ${files.length} arquivo(s)...`);
                const uploadPromises = files.map(async file => {
                    const filePath = `irregularity_notifications/${docRef.id}/${file.name}`;
                    const fileRef = ref(storage, filePath);
                    const metadata = { customMetadata: { 'createdBy': userData.uid } };
                    await uploadBytes(fileRef, file, metadata);
                    const url = await getDownloadURL(fileRef);
                    return { name: file.name, path: filePath, url };
                });
                const uploadedAttachments = await Promise.all(uploadPromises);
                await updateDoc(docRef, { attachments: uploadedAttachments });
            }

            await createNotificationsForRoles(
                ['administrador'],
                `Nova notificação ${notificationData.notificationNumber} registrada.`,
                'notificationNew',
                docRef.id
            );

            await logActivity('create', 'notification', docRef.id, null, notificationData.notificationNumber);

            setModalMessage("Notificação salva com sucesso!");
            setOperationSuccess(true);
        } catch (error) {
            console.error("Erro ao salvar notificação:", error);
            setModalMessage(`Erro ao salvar notificação: ${error.message}`);
            setOperationSuccess(false);
        } finally {
            setIsProcessing(false);
        }
    }, []);

    const onUpdateNotification = useCallback(async (notificationId, dataToUpdate, newFiles, removedFiles) => {
        const { db, storage, userData, setIsProcessing, setModalMessage, setOperationSuccess } = propsRef.current;
        setIsProcessing(true);
        try {
            const notificationRef = doc(db, `artifacts/${appId}/public/data/irregularity_notifications`, notificationId);
            
            // 1. Deletar arquivos removidos do Storage
            if (removedFiles && removedFiles.length > 0) {
                setModalMessage(`Removendo ${removedFiles.length} arquivo(s)...`);
                const deletePromises = removedFiles.map(file => {
                    if (file.path) { // Garante que o arquivo tem um caminho para ser deletado
                        return deleteObject(ref(storage, file.path));
                    }
                    return Promise.resolve();
                });
                await Promise.all(deletePromises);
            }

            // 2. Fazer upload de novos arquivos para o Storage
            const newAttachments = [];
            if (newFiles && newFiles.length > 0) {
                setModalMessage(`Enviando ${newFiles.length} novo(s) arquivo(s)...`);
                const uploadPromises = newFiles.map(async file => {
                    const filePath = `irregularity_notifications/${notificationId}/${file.name}`;
                    const fileRef = ref(storage, filePath);
                    const metadata = { customMetadata: { 'createdBy': userData.uid } };
                    await uploadBytes(fileRef, file, metadata);
                    const url = await getDownloadURL(fileRef);
                    return { name: file.name, path: filePath, url };
                });
                newAttachments.push(...await Promise.all(uploadPromises));
            }

            // 3. Atualizar o documento no Firestore
            const dataWithUpdateInfo = {
                ...dataToUpdate,
                updatedAt: serverTimestamp(),
                updatedBy: userData.uid,
            };

            // A lista de `attachments` em `dataToUpdate` já foi atualizada no componente.
            // Apenas adicionamos os novos uploads a ela.
            dataWithUpdateInfo.attachments = [...(dataToUpdate.attachments || []), ...newAttachments];

            const currentDoc = await getDoc(notificationRef);
            const oldStatus = currentDoc.exists() ? currentDoc.data().status : null;
            const notificationNumber = currentDoc.exists() ? currentDoc.data().notificationNumber : null;

            await updateDoc(notificationRef, dataWithUpdateInfo);

            const details = oldStatus !== dataToUpdate.status
                ? { statusChange: { from: oldStatus, to: dataToUpdate.status } }
                : null;

            await logActivity('update', 'notification', notificationId, details, notificationNumber);

            setModalMessage("Notificação atualizada com sucesso!");
            setOperationSuccess(true);
        } catch (error) {
            console.error("Erro ao atualizar notificação:", error);
            setModalMessage(`Erro ao atualizar notificação: ${error.message}`);
            setOperationSuccess(false);
        } finally {
            setIsProcessing(false);
        }
    }, []);

    return {
        onSaveProject,
        onUpdateProject,
        onUpdateProjectStatus,
        onDeleteProject,
        onDeleteProjectFile,
        onAddProjectComment,
        onUpdateProjectComment,
        onDeleteProjectComment,
        onReplaceProjectPoles,
        onSavePendency,
        onAddPendencyComment,
        onUpdatePendencyComment,
        onDeletePendencyComment,
        onUpdatePendency,
        onDeletePendency,
        onDeletePendencyFile,
        onUpdatePendencyField,
        onUpdateTrade,
        onToggleTradeCancellation,
        onFileDownload,
        onClearAllNotifications,
        onAssignNotification,
        onCloseNotification,
        onGenerateNotificationReport,
        onAbandonNotification,
        onSaveNotification,
        onUpdateNotification,
        onTogglePendencyPin,
        onMarkAsAnswered,
        onRestoreNotification,
    };
};
